import { Component, computed, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { StoreConfigService } from '@core/services/store-config.service';
import { AuthService } from '@core/services/auth.service';
import { CatalogNavService } from '@core/services/catalog-nav.service';
import { DEFAULT_ABOUT_TEXT } from '@core/utils/section-settings';
import { whatsappLink } from '@core/utils/whatsapp';
import { safeUrl } from '@core/utils/safe-url';

/**
 * Footer de la tienda con lo configurado en el admin (E-Commerce → Configuración):
 * "Sobre Nosotros", redes, WhatsApp y la dirección del negocio. Las opciones de la
 * tienda siguen las mismas reglas que el header (solo lo que tiene contenido).
 */
@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  imports: [RouterLink],
})
export class FooterComponent {
  configService = inject(StoreConfigService);
  auth = inject(AuthService);
  nav = inject(CatalogNavService);
  private router = inject(Router);

  /** La cuenta es global (/auth/*): tras entrar se vuelve a esta página de la tienda. */
  currentUrl = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );
  year = new Date().getFullYear();

  aboutText = computed(() => this.configService.config().aboutText || DEFAULT_ABOUT_TEXT);

  socials = computed(() => {
    const c = this.configService.config();
    return [
      { key: 'instagram', label: 'Instagram', href: safeUrl(c.instagramUrl) },
      { key: 'facebook', label: 'Facebook', href: safeUrl(c.facebookUrl) },
      { key: 'tiktok', label: 'TikTok', href: safeUrl(c.tiktokUrl) },
    ].filter((s): s is { key: string; label: string; href: string } => !!s.href);
  });

  whatsapp = computed(() => {
    const raw = this.configService.config().whatsappNumber || '';
    const href = whatsappLink(raw);
    return href ? { label: raw.trim(), href } : null;
  });

  address = computed(() => (this.configService.config().address || '').trim() || null);
  hasContact = computed(() => !!this.whatsapp() || !!this.address());
}
